import React, { FC, useState, useCallback } from 'react';
import { makeStyles } from 'tss-react/mui';
import { useSelector, shallowEqual } from 'react-redux';

import { categoriesTypes } from 'companion-app-components/flux/categories';
import type RootState from 'companion-app-components/utils/redux-store/rootState';
import { featureFlagsSelectors } from 'companion-app-components/flux/feature-flags';
import { transactionsUtils, transactionsTypes } from 'companion-app-components/flux/transactions';
import { isEntityShareUser } from 'companion-app-components/flux/account-groups/accountGroupsSelectors';

import QTypography from 'components/MUIWrappers/QTypography';
import CategoryField from 'components/QuickenControls/CategoryField';

import { isOldEdge } from 'utils/utils';

import CategoryMenuHeader from './CategoryMenuHeader';
import { styles } from './styles';

const isIe = require('is-iexplorer') || isOldEdge();  // eslint-disable-line

const categoryFieldInputProps = {
  style: {
    height: isIe ? 27 : 24,
  },
};

const useStyles = makeStyles()(styles as Record<string, any>);

interface RegCategoryFieldProps {
  defaultPropogateStatus: boolean;
  txn: transactionsTypes.CashFlowTransaction;
  wrapText: boolean;
  editable: boolean;
  forceStatic: boolean;
  longCats: boolean;
  showSplitWindow: boolean;
  inputRef: ((input) => void) | null;
  onChange: (event, type: string) => void;
  context: string;
}

const RegCategoryField: FC<RegCategoryFieldProps> = ({ 
  txn, 
  wrapText, 
  editable, 
  forceStatic, 
  longCats, 
  showSplitWindow, 
  inputRef, 
  onChange, 
  context,
  defaultPropogateStatus = false, 
}) => {

  const { classes, theme } = useStyles();

  const [propagateStatus, setPropagateStatus] = useState(defaultPropogateStatus);
  const showCategorySettings = useSelector((state: RootState) => featureFlagsSelectors.getFeatureFlags(state).get('showCategorySettings'), shallowEqual);
  const isEntitySharedUser = useSelector(isEntityShareUser);

  const handleHeaderCheckboxClick = () => setPropagateStatus(!propagateStatus);
  
  const categoryFieldHeader = (
    <CategoryMenuHeader
      checked={propagateStatus}
      boxClick={handleHeaderCheckboxClick}
      payee={txn.payee!}
    />
  );

  const categoryFieldMenuClose = useCallback(() => setPropagateStatus(false), []);

  const categoryFieldOnChange = useCallback((coa) => {
    const e = { target: { value: coa, doPropogate: propagateStatus } };
    onChange(e, 'category');
  }, [propagateStatus, onChange]);

  if (!transactionsUtils.isSplitTxn(txn)) {
    return (
      <CategoryField
        // @ts-expect-error: Property 'header' does not exist on type 'IntrinsicAttributes & CategoryFieldProps'.
        header={txn.payee?.length > 0 ? categoryFieldHeader : undefined}
        menuClose={categoryFieldMenuClose}
        onChange={categoryFieldOnChange}
        // @ts-expect-error: Property 'register' does not exist on type 'Components<BaseTheme>'.
        disableUnderline={theme.components?.register.noUnderline}
        name={editable ? 'regCatEditable' : 'static'}
        returnSelectionMethod
        editable={editable}
        createEnabled={!isEntitySharedUser && showCategorySettings}
        recommendedCategoryType={txn.amount > 0 ? categoriesTypes.CategoryTypeEnum.INCOME : categoriesTypes.CategoryTypeEnum.EXPENSE}
        clickable={!forceStatic && !showSplitWindow}
        value={txn.coa!}
        // @ts-expect-error: Property 'register' does not exist on type 'Components<BaseTheme>'.
        fontSize={theme.components?.register.fontSize.default}
        classNameWhenNotEditable={classes.regItemCore}
        autoFocus
        tooltip={!wrapText}
        longCats={longCats}
        inputRef={inputRef}
        InputProps={categoryFieldInputProps}
        context={context}
        txn={txn}
      />
    );
  }
  return (
    <QTypography
      clickable
      thinFont
      className={classes.regItemCore}
    >
      --- Split ---
    </QTypography>
  );
};

export default RegCategoryField;
